import { CONFIG, ZONE_ORDER } from "../CONFIG";

/**
 * The insight detectors. Each is a pure function of the insight context
 * (see trajectories.js) returning candidate insights — a kind, the
 * sub-topics it is about, the numbers behind it, and three 0..1 terms the
 * ranker multiplies: stakes (how severe the subject is), magnitude (how big
 * the change or gap is) and confidence (how much evidence sits behind it).
 *
 * Detectors reason in ZONES and POLARITY only — never in PM category names —
 * so a tenant with its own signal set (e.g. barriers / beliefs / impact)
 * gets the same insights in its own terms.
 */

const K = CONFIG.INSIGHTS;
const HIGH = new Set(["fires", "chronic"]);
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const stakesOf = (severity) => clamp01(severity / K.STAKES_CEILING);
const rank = (zone) => ZONE_ORDER.indexOf(zone);
const at = (arr, fromEnd) => arr[arr.length - fromEnd];
const candidate = (kind, subjects, rest) => ({
  kind,
  id: `${kind}:${subjects.map((s) => s.nodeId).join(",")}`,
  subjectIds: subjects.map((s) => s.nodeId),
  subject: subjects[0],
  subjects,
  ...rest,
});

const priorities = (ctx) => [...ctx.trajectories.values()].filter((t) => t.item && !t.item.isWin);
const wins = (ctx) => [...ctx.trajectories.values()].filter((t) => t.item && t.item.isWin);

// Entered Fires this period (from another zone, or newly scoreable).
const caughtFire = (ctx) =>
  priorities(ctx).flatMap(({ item, points }) => {
    const cur = at(points, 1);
    const prev = at(points, 2);
    if (cur.zone !== "fires" || prev?.zone === "fires") return [];
    return [
      candidate("caughtFire", [item], {
        numbers: { index: cur.index, prevZone: prev?.zone || null, n: item.n, docs: item.severity.docs },
        stakes: stakesOf(item.severity.value),
        magnitude: clamp01(cur.index / K.INDEX_CEILING),
        confidence: cur.confidence,
      }),
    ];
  });

// Any other zone change since last period, weighted by how far it moved.
const zoneMove = (ctx) =>
  priorities(ctx).flatMap(({ item, points }) => {
    const cur = at(points, 1);
    const prev = at(points, 2);
    if (!cur.zone || !prev?.zone || cur.zone === prev.zone || cur.zone === "fires") return [];
    return [
      candidate("zoneMove", [item], {
        numbers: { fromZone: prev.zone, toZone: cur.zone, index: cur.index },
        stakes: stakesOf(item.severity.value),
        magnitude: clamp01(Math.abs(rank(cur.zone) - rank(prev.zone)) / (ZONE_ORDER.length - 1)),
        confidence: Math.min(cur.confidence, prev.confidence),
      }),
    ];
  });

// Severe and cooling for two or more periods running — the validate loop.
const resolving = (ctx) =>
  priorities(ctx).flatMap(({ item, points }) => {
    const cur = at(points, 1);
    if (cur.zone !== "resolving") return [];
    let run = 0;
    for (let i = points.length - 1; i >= 0 && points[i].band === "cooling"; i -= 1) run += 1;
    if (run < K.RESOLVING_MIN_PERIODS) return [];
    return [
      candidate("resolving", [item], {
        numbers: { index: cur.index, periodsCooling: run, since: points[points.length - run].label },
        stakes: stakesOf(item.severity.value),
        magnitude: clamp01(Math.abs(cur.index) / K.INDEX_CEILING),
        confidence: cur.confidence,
      }),
    ];
  });

// Was Fires or Chronic, then nothing at all for the last periods.
const goneSilent = (ctx) =>
  priorities(ctx).flatMap(({ item, points }) => {
    const silent = points.slice(-K.SILENT_PERIODS);
    const earlier = points.slice(0, -K.SILENT_PERIODS);
    if (silent.length < K.SILENT_PERIODS || !silent.every((p) => p.n === 0)) return [];
    const loud = earlier.filter((p) => HIGH.has(p.zone));
    if (!loud.length) return [];
    const peak = loud.reduce((a, b) => (b.n > a.n ? b : a), loud[0]);
    const lastActive = [...earlier].reverse().find((p) => p.n > 0);
    return [
      candidate("goneSilent", [item], {
        numbers: { peakZone: peak.zone, peakN: peak.n, loudPeriods: loud.length, lastActive: lastActive?.label || null, silentPeriods: K.SILENT_PERIODS },
        stakes: stakesOf(item.severity.value),
        magnitude: 1,
        confidence: clamp01(peak.n / CONFIG.CONFIDENCE_FLOOR),
      }),
    ];
  });

// One sub-topic carries most of a topic, or most of a topic sits in the
// severe zones.
const concentration = (ctx) =>
  ctx.buckets.flatMap((b) => {
    const rows = b.rows.filter((r) => !r.isWin);
    if (rows.length < K.MIN_TOPIC_ROWS || !b.item) return [];
    const total = rows.reduce((s, r) => s + r.n, 0);
    const top = [...rows].sort((x, y) => y.n - x.n)[0];
    const high = rows.filter((r) => HIGH.has(r.zone));
    const topShare = total ? top.n / total : 0;
    const highShare = high.length / rows.length;
    if (topShare < K.CONCENTRATION_TOP_SHARE && highShare < K.CONCENTRATION_ZONE_SHARE) return [];
    return [
      candidate("concentration", rows, {
        topic: b,
        numbers: { rowCount: rows.length, highCount: high.length, highShare, top, topShare, total },
        stakes: stakesOf(b.item.severity.value),
        magnitude: clamp01(Math.max(topShare, highShare)),
        confidence: clamp01(total / (K.MIN_TOPIC_ROWS * CONFIG.CONFIDENCE_FLOOR)),
      }),
    ];
  });

// The same sub-topic moving differently in two datasets.
const channelContrast = (ctx) =>
  priorities(ctx).flatMap(({ item }) => {
    if (item.datasets.length < 2) return [];
    const scored = item.datasets
      .map((ds) => ({ dataset: ds, item: ctx.byDataset.get(ds)?.get(item.nodeId) }))
      .filter((x) => x.item?.momentum.available);
    let best = null;
    for (let i = 0; i < scored.length; i += 1) {
      for (let j = i + 1; j < scored.length; j += 1) {
        const a = scored[i];
        const b = scored[j];
        const risingA = a.item.momentum.band === "rising" || a.item.momentum.band === "new";
        const risingB = b.item.momentum.band === "rising" || b.item.momentum.band === "new";
        if (risingA === risingB) continue;
        const gap = Math.abs(a.item.momentum.index - b.item.momentum.index);
        if (!best || gap > best.gap) best = { a: risingA ? a : b, b: risingA ? b : a, gap };
      }
    }
    if (!best) return [];
    const side = (x) => ({ dataset: x.dataset, index: x.item.momentum.index, band: x.item.momentum.band, n: x.item.momentum.nNow, item: x.item });
    return [
      candidate("channelContrast", [item], {
        numbers: { rising: side(best.a), other: side(best.b), gap: best.gap },
        stakes: stakesOf(item.severity.value),
        magnitude: clamp01(best.gap / K.INDEX_CEILING),
        confidence: Math.min(best.a.item.momentum.confidence, best.b.item.momentum.confidence),
      }),
    ];
  });

// A topic whose leading signal category turned negative over the window —
// a want or a habit that became a pain (or, on another taxonomy, a belief
// that became a barrier).
const escalation = (ctx) =>
  ctx.buckets.flatMap((b) => {
    if (!b.item) return [];
    const usable = b.leadingByPeriod.filter((p) => p.total >= K.MIN_CATEGORY_N && p.leading);
    if (usable.length < 2) return [];
    const first = usable[0];
    const last = usable[usable.length - 1];
    if (ctx.polarityOf(first.leading) === "negative" || ctx.polarityOf(last.leading) !== "negative") return [];
    const negShare = (p) =>
      Object.entries(p.counts).reduce((s, [cat, n]) => s + (ctx.polarityOf(cat) === "negative" ? n : 0), 0) / p.total;
    return [
      candidate("escalation", b.rows, {
        topic: b,
        numbers: { from: first, to: last, negativeShareFrom: negShare(first), negativeShareTo: negShare(last) },
        stakes: stakesOf(b.item.severity.value),
        magnitude: clamp01(negShare(last) - negShare(first)),
        confidence: clamp01(Math.min(first.total, last.total) / (2 * CONFIG.CONFIDENCE_FLOOR)),
      }),
    ];
  });

// Many observations from one or two conversations (a loud voice), or one
// observation each from many conversations (a quiet consensus).
const breadthDepth = (ctx) =>
  priorities(ctx).flatMap(({ item }) => {
    const docs = item.severity.docs;
    const loud = item.n >= K.LOUD_MIN_N && docs <= K.LOUD_MAX_DOCS;
    const quiet = docs >= K.QUIET_MIN_DOCS && docs / item.n >= K.QUIET_DOC_RATIO;
    if (!loud && !quiet) return [];
    return [
      candidate("breadthDepth", [item], {
        numbers: { variant: loud ? "loud" : "quiet", n: item.n, docs },
        stakes: stakesOf(item.severity.value),
        magnitude: K.BREADTH_MAGNITUDE,
        confidence: clamp01(item.n / (2 * CONFIG.CONFIDENCE_FLOOR)),
      }),
    ];
  });

// A good-news sub-topic cooling, paired with the most severe pain in the
// same topic when there is one.
const winSlipping = (ctx) =>
  wins(ctx).flatMap(({ item, points }) => {
    const cur = at(points, 1);
    if (cur.band !== "cooling") return [];
    const topic = ctx.bucketOfNode.get(item.nodeId);
    const pair = topic?.rows.filter((r) => !r.isWin).sort((a, b) => b.severity.value - a.severity.value)[0] || null;
    return [
      candidate("winSlipping", pair ? [item, pair] : [item], {
        numbers: { index: cur.index, pair },
        stakes: Math.max(K.WIN_STAKES_FLOOR, pair ? stakesOf(pair.severity.value) : 0),
        magnitude: clamp01(Math.abs(cur.index) / K.INDEX_CEILING),
        confidence: cur.confidence,
      }),
    ];
  });

export const DETECTORS = [caughtFire, zoneMove, resolving, goneSilent, concentration, channelContrast, escalation, breadthDepth, winSlipping];

export const detectInsights = (ctx) => (ctx ? DETECTORS.flatMap((detect) => detect(ctx)) : []);
